import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { Dimensions, Platform, Text, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import Animated from 'react-native-reanimated';
import { useDispatch, useSelector } from 'react-redux';

import { RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { isFuture, secondsToMilliseconds } from 'date-fns';

import DetailSkeletonLoadingDesktop from 'assets/images/animation/detail_page_loading_desktop.svg';
import DetailSkeletonLoadingMobile from 'assets/images/animation/detail_page_loading_mobile.svg';
import DetailSkeletonLoadingTablet from 'assets/images/animation/detail_page_loading_tablet.svg';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';
import {
    DETAIL_AUDIO_LANGUAGE_TEXT,
    DETAIL_ERROR_CANCEL,
    DETAIL_ERROR_CODE,
    DETAIL_ERROR_DESCRIPTION,
    DETAIL_ERROR_TITLE,
    DETAIL_METADATA_ID,
    DETAIL_PROGRAM_TITLE_ID,
    DETAIL_SKELETON_ID,
    DETAIL_SUBTITLES_TEXT,
} from 'config/testID';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { getBookmarks } from 'store/bookmarks/actions';
import { selectNotCompletedBookmarkByContentId } from 'store/bookmarks/selectors';
import {
    selectLayoutConfig,
    selectScreenReaderEnabled,
} from 'store/config/selectors';
import { setDetailsEpisode } from 'store/details/actions';
import { showPlayer } from 'store/player/actions';
import { StreamTypes } from 'store/player/actions.types';
import {
    selectPlayerConfig,
    selectSpotlightVideoVisible,
} from 'store/player/selectors';
import { selectChannelsById } from 'store/programs/selectors';
import {
    RBEditorial,
    RBProgram,
    RBTechnical,
} from 'store/railsBuilder/reducer.types';
import {
    RecordingItem,
    RecordingMetadata,
} from 'store/recordings/reducer.types';
import {
    selectDeleteSeriesRecordingRequestStatus,
    selectRecordingDeleteApiStatus,
} from 'store/recordings/selectors';
import {
    endSentryTransaction,
    startSentryTransaction,
} from 'store/sentry/actions';
import { SENTRY_TRANSACTIONS } from 'store/sentry/constants';
import { ReduxStore } from 'store/types';
import { getEntitlements } from 'store/user/actions';
import {
    selectEntitlements,
    selectIfCollectionSuspensionActive,
    selectIfKidsProfileActive,
    selectLanguage,
} from 'store/user/selectors';
import { RequestStatus } from 'store/utils';

import isCatchupChannel from 'services/data-manager/catchup/isCatchupChannel';
import isCatchupDetails from 'services/data-manager/catchup/isCatchupDetails';
import parseVideoItem from 'services/data-manager/parsing/parseVideoItem';
import parseVideoItemForCU from 'services/data-manager/parsing/parseVideoItemForCU.ts';
import parseParentalRating from 'services/data-manager/parsing/utils/parseParentalRating';
import {
    VideoItem,
    VodEditorial,
    isSeriesProgram,
} from 'services/data-manager/types';

import { useCast } from 'utils/cast/useCast';
import checkVodEntitlement from 'utils/checkVodEntitlement';
import { isAssetVod } from 'utils/checkVodType';
import { ERROR_CODE_A014 } from 'utils/error/errorCodes.ts';
import getChannelId from 'utils/getChannelId';
import getContentType from 'utils/getContentType';
import getCorrectProgramStartAndEndTime from 'utils/getCorrectProgramStartAndEndTime';
import getVodDetailsExpiryDate from 'utils/getVodDetailsExpiryDate';
import getVodDetailsStartTime from 'utils/getVodDetailsStartTime';
import useAnimatedHeader from 'utils/hooks/useAnimatedHeader';
import useHeaderConfig from 'utils/hooks/useHeaderConfig';
import useIsLive from 'utils/hooks/useIsLive';
import useMetadata from 'utils/hooks/useMetadata';
import useNavigateBackOnHardwareBackPress from 'utils/hooks/useNavigateBackOnHardwareBackPress';
import useProfileParentalRating from 'utils/hooks/useProfileParentalRating';
import useRecordings from 'utils/hooks/useRecordings';
import useScreenFocused from 'utils/hooks/useScreenFocused';
import isSubscribedToChannel from 'utils/isSubscribedToChannel';
import needsToLockContent from 'utils/parentalControl/needsToLockContent';
import {
    getDefaultMetadata,
    isRecordingItem,
    selectRecordingMetadataBaseOnLanguage,
} from 'utils/recordings';
import {
    heightPercentageToDP,
    isTablet,
    isTabletOrWeb,
    isWeb,
    widthPercentageToDP,
} from 'utils/responsiveness';

import { noSeasonName } from 'screens/Detail/components/Seasons/constants';
import useCatchupDetails from 'screens/Detail/hooks/useCatchupDetails';
import { isLive } from 'screens/Epg/utils/program';
import useCloseModalPlayerOnDetailsPage from 'screens/PlayerModal/hooks/useCloseModalPlayerOnDetailsPage';
import { RecordingType } from 'screens/PlayerModal/types';

import ContentMarkerIcons from 'components/ContentMarkerIcons';
import DescriptionOverlay from 'components/Description';
import DescriptionMore from 'components/DescriptionMore';
import DialogView from 'components/Dialog';
import Marker from 'components/Marker';
import DetailsNavigationHeaderLeft from 'components/NavigationHeader/DetailsHeaderComponents/Left';
import SimpleNavigationHeaderRight from 'components/NavigationHeader/SimpleNavigationHeader/Right';
import useNoInternetAlertVisible from 'components/NoInternetAlert/hooks/useNoInternetAlertVisible';
import RecordingsNotificationsAndOverlay from 'components/RecordingsNotificationsAndOverlays';
import useRecordingsNotificationsAndOverlays from 'components/RecordingsNotificationsAndOverlays/hooks/useRecordingsNotificationsAndOverlays';
import SkeletonLoading from 'components/SkeletonLoading';

import DetailCast from './components/DetailCast';
import DetailPageButtons from './components/DetailPageButtons';
import DetailPageChannelLogo from './components/DetailPageChannelLogo';
import DetailPageImage from './components/DetailPageImage';
import DetailPageSubTitle from './components/DetailPageSubTitle';
import DetailPageTitle from './components/DetailPageTitle';
import DetailRootView from './components/DetailRootView';
import { Seasons } from './components/Seasons';
import useDetailsData from './hooks/useDetailsData';
import useDetailsImageId from './hooks/useDetailsImageId';
import {
    CLOSE_BUTTON,
    DELETED_EPISODE_NOTIFICATION_TITLE,
    DELETED_NOTIFICATION_TITLE,
    ERR_A012_DETAIL_DISPLAY_ERROR,
    ERR_A012_DETAIL_DISPLAY_ERROR_DESC,
    ERR_A014_DETAIL_DISPLAY_ERROR,
    ERR_A014_DETAIL_DISPLAY_ERROR_DESC,
    SCREEN_AUDIO_SUBTITLES_SUBTITLES,
    SETTINGS_AUDIO_LANGUAGE,
} from './languageConst';
import styles from './style';

type Props = {
    navigation: StackNavigationProp<RouteNavigationParams, SCREEN.DETAILS>;
    route: RouteProp<RouteNavigationParams, SCREEN.DETAILS>;
};

const {
    ACCESSIBILITY: {
        LOGIN: { ACCESSIBILITY_CLOSE_BUTTON },
    },
    TRY_AGAIN_BUTTON,
} = LANGUAGE_KEYS;

function DetailPage({ navigation, route }: Props) {
    const dispatch = useDispatch();
    const { pageScrollPosition, onScroll } = useAnimatedHeader();
    const { t } = useTranslation();
    const { screenFocused } = useScreenFocused();

    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const { isCastConnected, cast } = useCast();

    const {
        params: { templateId, railId, playbackType, channelId },
    } = route;

    const isVod = isAssetVod(playbackType);

    const { primary, background } = useSelector(selectLayoutConfig);
    const channelsById = useSelector(selectChannelsById);
    const entitlements = useSelector(selectEntitlements);
    const kidsProfileActive = useSelector(selectIfKidsProfileActive);
    const currentLanguage = useSelector(selectLanguage);
    const collectionSuspensionActive = useSelector(
        selectIfCollectionSuspensionActive,
    );
    const deleteRecordingRequestStatus = useSelector(
        selectRecordingDeleteApiStatus,
    );
    const deleteSeriesRecordingRequestStatus = useSelector(
        selectDeleteSeriesRecordingRequestStatus,
    );
    const { visible: playerVisible } = useSelector(selectPlayerConfig);
    const spotlightPlayerVisible = useSelector(selectSpotlightVideoVisible);
    const screenReaderEnabled = useSelector(selectScreenReaderEnabled);

    const prevDeleteRecordingRequestStatus = useRef<
        typeof deleteRecordingRequestStatus
    >(deleteRecordingRequestStatus);
    const prevDeleteSeriesRecordingRequestStatus = useRef<
        typeof deleteSeriesRecordingRequestStatus
    >(deleteSeriesRecordingRequestStatus);

    useCloseModalPlayerOnDetailsPage();

    const {
        detailsLoading,
        detailsError,
        details,
        castAndCrew,
        descriptionLoading,
        onDescriptionLoaded,
        onRetry,
    } = useDetailsData(route.params);

    const detailsIsRecording = isRecordingItem(details);
    const detailsMetadata:
        | RBProgram
        | VodEditorial
        | RecordingMetadata
        | undefined = detailsIsRecording
        ? {
              ...getDefaultMetadata(details),
              ...selectRecordingMetadataBaseOnLanguage(
                  details.metadata,
                  currentLanguage,
              ),
          }
        : details;
    const detailsId = detailsIsRecording ? details.contentId : details?.id;

    const bookmark = useSelector((state: ReduxStore) =>
        selectNotCompletedBookmarkByContentId(
            state,
            detailsIsRecording ? details.eventId : details?.id,
        ),
    );

    const {
        // single recording data
        recordingItem,
        isRecordingLoading,
        canWatchRecording,
        recordingIsProtected,
        // series recording data
        seriesId,
        seriesHasProtectedRecording,
        // handlers for single recording
        handleCancelRecording,
        handleUnprotectRecording,
        handleDeleteRecording,
        handleDeleteSeriesRecording,
        // handlers for series recording
        handleCancelSeriesRecording,
        // dialogs and notifications
        recordingsNotification,
        onCallbackSetNotification,
        showQuotaDialog,
        showCancelEpisodeDialog,
        showCancelSeriesDialog,
        showDeleteRecordingDialog,
        showDeleteSeriesDialog,
        resetNotificationCallback,
        hideCancelEpisodeDialogCallback,
        hideCancelSeriesDialogCallback,
        hideQuotaDialogCallback,
        hideDeleteRecordingDialogCallback,
        hideDeleteSeriesDialogCallback,
        // buttons
        recordingsButtonsList,
        onManageRecordingPress,
        // dropdown
        isRecordingDropdownVisible,
        showRecordingDropdown,
        hideRecordingDropdown,
        // permissions
        hasGlobalRecordingPermission,
        getAssetTitle,
    } = useRecordings({
        item: details,
        type: RecordingType.DETAIL,
        playbackType,
    });

    const {
        pinDialogProps,
        canCloseButtonDropdown,
        onManageRecordingPressCallback,
        handleShowPinDialog,
    } = useRecordingsNotificationsAndOverlays({
        handleDeleteRecording,
        handleDeleteSeriesRecording,
        handleUnprotectRecording,
        onManageRecordingPress,
    });

    const channelIdFromItem = getChannelId(detailsMetadata);

    const channel = channelIdFromItem
        ? channelsById[channelIdFromItem]
        : undefined;

    const imageContentId = useDetailsImageId(details);
    const { programStartTime, programEndTime } =
        getCorrectProgramStartAndEndTime(detailsMetadata);

    const { isCatchup, technicalDetailsCatchup, ableToPlayCatchup } =
        useCatchupDetails(detailsMetadata, programEndTime);

    const [descriptionVisible, setDescriptionVisible] = useState(false);

    useCloseModalPlayerOnDetailsPage();

    useHeaderConfig({
        pageScrollPosition: screenReaderEnabled
            ? undefined
            : pageScrollPosition,
        headerTransparent: !screenReaderEnabled,
        headerLeft: () => (
            <DetailsNavigationHeaderLeft
                navigation={navigation}
                detailsLoading={detailsLoading || descriptionLoading}
            />
        ),
        headerRight: () => (
            <SimpleNavigationHeaderRight
                needsClose={false}
                detailsLoading={detailsLoading || descriptionLoading}
            />
        ),
        isRoot: isWeb,
        screenFocused,
    });

    useNavigateBackOnHardwareBackPress();

    const { visible: isNoInternetAlertVisible } =
        useNoInternetAlertVisible(false);

    const isLiveDetails = useIsLive({
        startTime: programStartTime,
        endTime: programEndTime,
    });

    const isChannelSubscribed = !!(
        channel && isSubscribedToChannel(channel, entitlements)
    );
    const ableToPlayLive = !!(
        isLiveDetails &&
        channelId &&
        channelsById[channelId] &&
        isChannelSubscribed
    );

    const isStartOver = !!(
        ableToPlayLive &&
        details &&
        'isStartOver' in details &&
        details.isStartOver
    );

    const ableToPlayVod = useMemo(() => {
        return !!(
            details &&
            (details as RBEditorial).technicals &&
            isVod &&
            checkVodEntitlement(details as RBEditorial, entitlements)
        );
    }, [details, entitlements, isVod]);

    const vodExpiryDate = useMemo(() => {
        if (ableToPlayVod) {
            const expiryDate = getVodDetailsExpiryDate(
                (details as RBEditorial).technicals as RBTechnical[],
                entitlements,
            );

            return expiryDate;
        }

        return undefined;
    }, [ableToPlayVod, details, entitlements]);

    const hasSubtitleData = !!detailsMetadata?.Subtitles?.length;

    const parentalRating = parseParentalRating(detailsMetadata, channel);
    const profileParentalControlRating = useProfileParentalRating();
    const isContentLocked = needsToLockContent(
        profileParentalControlRating,
        parentalRating,
    );

    const initialSeason = useMemo<
        typeof noSeasonName | number | undefined
    >(() => {
        if (isSeriesProgram(details)) {
            const detailsSeasonNumber = isRecordingItem(details)
                ? details.metadata.hu_HU?.SeasonNumber
                : details.SeasonNumber;

            if (
                detailsSeasonNumber != null &&
                Number(detailsSeasonNumber) > 0
            ) {
                return Number(detailsSeasonNumber);
            }

            return noSeasonName;
        }

        return undefined;
    }, [details]);

    const backgroundColorStyle = useMemo(
        () => ({ backgroundColor: background }),
        [background],
    );

    const videoDuration = useMemo(() => {
        if (isCatchup && technicalDetailsCatchup) {
            return technicalDetailsCatchup.duration || 0;
        }

        if (details && isVod) {
            return (details as RBEditorial).NetDuration || 0;
        }

        if (detailsIsRecording) {
            return details.captureDuration || 0;
        }

        return 0;
    }, [isCatchup, details, isVod, detailsIsRecording]);

    const metadata = useMetadata(details, isVod);
    const hasSeasonStripe = !!(
        templateId &&
        route.params.seriesId &&
        initialSeason != null
    );

    const vodStartTime = getVodDetailsStartTime(
        (details as RBEditorial)?.technicals as RBTechnical[],
    );
    const isComingSoon =
        vodStartTime && isFuture(secondsToMilliseconds(vodStartTime));

    const handleShowDescription = useCallback(() => {
        setDescriptionVisible(true);
        // Needed to be able to close the description overlay with browser back button
        if (isWeb) {
            window.history.pushState({}, '', null);
        }
    }, []);

    const handleHideDescription = useCallback(() => {
        setDescriptionVisible(false);
    }, []);

    const handleHideDescriptionInBrowser = useCallback(() => {
        window.history.back();
    }, []);

    const handleSelectEpisode = useCallback(
        (episode: RBProgram | RecordingItem) => {
            if (templateId) {
                dispatch(getBookmarks());
                dispatch(setDetailsEpisode(templateId, episode));
            }
        },
        [templateId],
    );

    useEffect(() => {
        dispatch(endSentryTransaction(SENTRY_TRANSACTIONS.DetailPageRendering));
        dispatch(getEntitlements({ autoRetry: true }));
    }, []);

    useEffect(() => {
        if (isWeb && descriptionVisible) {
            window.addEventListener('popstate', handleHideDescription);
        }

        return () => {
            if (isWeb) {
                window.removeEventListener('popstate', handleHideDescription);
            }
        };
    }, [descriptionVisible]);

    useEffect(() => {
        if (
            screenFocused &&
            deleteRecordingRequestStatus === RequestStatus.SUCCESS &&
            prevDeleteRecordingRequestStatus.current === RequestStatus.PENDING
        ) {
            onCallbackSetNotification({
                title: t(
                    seriesId
                        ? DELETED_EPISODE_NOTIFICATION_TITLE
                        : DELETED_NOTIFICATION_TITLE,
                ),
                description: getAssetTitle(),
            });
        }

        prevDeleteRecordingRequestStatus.current = deleteRecordingRequestStatus;
    }, [deleteRecordingRequestStatus]);

    useEffect(() => {
        if (
            screenFocused &&
            deleteSeriesRecordingRequestStatus === RequestStatus.SUCCESS &&
            prevDeleteSeriesRecordingRequestStatus.current ===
                RequestStatus.PENDING
        ) {
            onCallbackSetNotification({
                title: t(DELETED_NOTIFICATION_TITLE),
                description: getAssetTitle(true),
            });
        }

        prevDeleteSeriesRecordingRequestStatus.current =
            deleteSeriesRecordingRequestStatus;
    }, [deleteSeriesRecordingRequestStatus]);

    const callPlayer = useCallback(
        (videoItem: VideoItem, playerPlaybackType: StreamTypes) => {
            if (isCastConnected) {
                cast(videoItem, playerPlaybackType);
            } else {
                dispatch(
                    startSentryTransaction(SENTRY_TRANSACTIONS.ChannelTune),
                );
                dispatch(
                    showPlayer({
                        videoItem,
                        playbackType: playerPlaybackType,
                        railId,
                        templateId,
                    }),
                );
            }
        },
        [templateId, railId, dispatch, isCastConnected, cast],
    );

    const handleWatchPress = useCallback(() => {
        const assetDetails = details as RBProgram | VodEditorial | undefined;

        if (details && channelIdFromItem) {
            if (isLive(detailsMetadata) && channel) {
                callPlayer(
                    parseVideoItem({
                        channel,
                        program: assetDetails,
                        bookmark,
                    }),
                    StreamTypes.live,
                );
            } else if (
                channel &&
                assetDetails &&
                isCatchupChannel(channel) &&
                isCatchupDetails(assetDetails)
            ) {
                callPlayer(
                    parseVideoItemForCU(
                        parseVideoItem({
                            channel,
                            program: assetDetails,
                            bookmark,
                        }),
                    ),
                    StreamTypes.catchup,
                );
            } else if (channel && detailsIsRecording && canWatchRecording) {
                callPlayer(
                    parseVideoItem({
                        channel,
                        recording: details,
                        recordingMetadata: {
                            ...getDefaultMetadata(details),
                            ...selectRecordingMetadataBaseOnLanguage(
                                details.metadata,
                                currentLanguage,
                            ),
                        },
                        streamType: StreamTypes.npvr,
                        bookmark,
                    }),
                    StreamTypes.npvr,
                );
            }
        } else if (
            assetDetails &&
            channel &&
            isCatchupChannel(channel) &&
            isCatchupDetails(assetDetails)
        ) {
            const videoItem = parseVideoItemForCU(
                parseVideoItem({
                    channel,
                    program: assetDetails,
                    bookmark,
                }),
            );

            if (isCastConnected) {
                cast(videoItem, StreamTypes.catchup);
            } else {
                dispatch(
                    startSentryTransaction(
                        SENTRY_TRANSACTIONS.CatchupPlaybackTime,
                    ),
                );
                dispatch(
                    showPlayer({
                        videoItem,
                        playbackType: StreamTypes.catchup,
                        railId,
                        templateId,
                    }),
                );
            }
        } else if (details && isVod) {
            const videoItem = parseVideoItem({
                channel,
                program: assetDetails,
                streamType: StreamTypes.vod,
                bookmark,
            });

            if (isCastConnected) {
                cast(videoItem, StreamTypes.vod);
            } else {
                dispatch(
                    startSentryTransaction(SENTRY_TRANSACTIONS.VODPlaybackTime),
                );
                dispatch(
                    showPlayer({
                        videoItem,
                        playbackType: StreamTypes.vod,
                        railId,
                        templateId,
                    }),
                );
            }
        }
    }, [
        details,
        detailsIsRecording,
        canWatchRecording,
        channelIdFromItem,
        channel,
        isVod,
        detailsMetadata,
        callPlayer,
        bookmark,
        currentLanguage,
        isCastConnected,
        cast,
        dispatch,
        railId,
        templateId,
    ]);

    const renderDetailsMarker = useCallback(
        () => (
            <Marker
                bgColor={primary}
                style={styles.marker}
                startTime={(!isVod && programStartTime) || undefined}
                endTime={(!isVod && programEndTime) || undefined}
                expiryDate={(isVod && vodExpiryDate) || undefined}
                comingSoonDate={
                    (isVod && detailsMetadata?.period?.start) || undefined
                }
            />
        ),
        [
            programStartTime,
            programEndTime,
            vodExpiryDate,
            detailsMetadata?.period?.start,
            isVod,
            primary,
        ],
    );

    const shouldMoveTabletStripeUp = useMemo(() => {
        return (
            !((ableToPlayLive || ableToPlayCatchup) && isChannelSubscribed) &&
            !detailsMetadata?.Description &&
            Platform.OS === 'android'
        );
    }, [
        ableToPlayLive,
        ableToPlayCatchup,
        isChannelSubscribed,
        detailsMetadata,
    ]);

    // useEffect to set the description loading to false after 500 ms when the details are loaded.
    // This timeout is used as a safety measure to ensure enough time for the description to load.
    // If the description loads faster than 500 ms, it will be set to true before this timeout.
    useEffect(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        if (!detailsLoading && descriptionLoading) {
            timeoutRef.current = setTimeout(() => {
                onDescriptionLoaded();
            }, 500);
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [detailsLoading, descriptionLoading, onDescriptionLoaded]);

    const handleCloseErrorDialog = useCallback(() => {
        if (navigation.canGoBack()) {
            navigation.goBack();
        } else if (isWeb) {
            window.history.back();
        } else {
            navigation.reset({
                routes: [{ name: SCREEN.ROOT }],
            });
        }
    }, [navigation]);

    const renderAudioSubtitlesAccessibilityText = useCallback(
        (language: string, isLast: boolean, isAudio = false) => (
            <Text
                style={styles.metadataText}
                key={language}
                accessibilityLabel={t(
                    `${
                        isAudio ? 'AUDIO' : 'SUBTITLES'
                    }_LANGUAGE_OTT_${language.toUpperCase()}`,
                )}
                accessibilityRole="text"
                accessible
                {...(isWeb && { tabIndex: 0 })}
            >
                {language}
                {isLast ? '' : ' '}
            </Text>
        ),
        [t],
    );

    if (detailsError) {
        return (
            <DialogView
                style={styles.errorDialog}
                title={
                    detailsError?.code === ERROR_CODE_A014
                        ? ERR_A014_DETAIL_DISPLAY_ERROR
                        : ERR_A012_DETAIL_DISPLAY_ERROR
                }
                description={
                    detailsError?.code === ERROR_CODE_A014
                        ? ERR_A014_DETAIL_DISPLAY_ERROR_DESC
                        : ERR_A012_DETAIL_DISPLAY_ERROR_DESC
                }
                actionButtonTitle={CLOSE_BUTTON}
                actionButtonFillColor={primary}
                actionButtonTextStyle={styles.errorButtonText}
                actionButtonOnPress={handleCloseErrorDialog}
                actionButtonAccessibilityLabel={t(ACCESSIBILITY_CLOSE_BUTTON)}
                cancelButtonTitle={
                    detailsError?.code === ERROR_CODE_A014
                        ? TRY_AGAIN_BUTTON
                        : undefined
                }
                cancelButtonAccessibilityLabel={
                    detailsError?.code === ERROR_CODE_A014
                        ? TRY_AGAIN_BUTTON
                        : undefined
                }
                cancelButtonOnPress={onRetry}
                cancelButtonBordered
                errorCode={detailsError?.code}
                titleTestID={DETAIL_ERROR_TITLE}
                descriptionTestID={DETAIL_ERROR_DESCRIPTION}
                actionButtonTestID={DETAIL_ERROR_CANCEL}
                errorCodeTestID={DETAIL_ERROR_CODE}
            />
        );
    }

    return (
        <DetailRootView
            style={[gStyles.flex, backgroundColorStyle]}
            importantForAccessibility={
                details &&
                detailsMetadata &&
                !playerVisible &&
                !spotlightPlayerVisible
                    ? 'auto'
                    : 'no-hide-descendants'
            }
            accessibilityElementsHidden={
                !(
                    details &&
                    detailsMetadata &&
                    !playerVisible &&
                    !spotlightPlayerVisible
                )
            }
        >
            {!!(details && detailsMetadata) && (
                <>
                    <Animated.ScrollView
                        scrollEventThrottle={16}
                        showsVerticalScrollIndicator={false}
                        onScroll={onScroll}
                        nestedScrollEnabled
                        removeClippedSubviews
                        bounces
                    >
                        <View
                            style={[
                                gStyles.container,
                                styles.container,
                                backgroundColorStyle,
                            ]}
                        >
                            <View style={gStyles.container}>
                                <DetailPageImage
                                    contentId={imageContentId}
                                    contentType={getContentType(details)}
                                />
                                <View style={styles.detailsPageContent}>
                                    <DetailPageChannelLogo
                                        channelId={channelId}
                                        channelName={
                                            channel?.editorial.longName
                                        }
                                    />
                                    <DetailPageTitle
                                        testID={DETAIL_PROGRAM_TITLE_ID}
                                        item={detailsMetadata}
                                        detailsLoading={
                                            detailsLoading || descriptionLoading
                                        }
                                        screenFocused={
                                            screenFocused &&
                                            !playerVisible &&
                                            !spotlightPlayerVisible
                                        }
                                    />
                                    {!!(
                                        (detailsMetadata as RecordingMetadata)
                                            .episode ||
                                        (detailsMetadata as RBProgram)
                                            .Episode ||
                                        detailsMetadata.episodeNumber ||
                                        detailsMetadata.SeasonNumber
                                    ) && (
                                        <DetailPageSubTitle
                                            item={detailsMetadata}
                                            recordingItem={recordingItem}
                                        />
                                    )}
                                    <View style={styles.contentMarkers}>
                                        {renderDetailsMarker()}
                                        <ContentMarkerIcons
                                            parentalRating={parentalRating}
                                            iconStyle={
                                                styles.contentMarkerIcons
                                            }
                                            shouldDisplayCatchup={isCatchup}
                                            shouldDisplayStartOver={isStartOver}
                                            recordingItem={recordingItem}
                                            shouldDisplaySubscribe={
                                                !isVod && !isChannelSubscribed
                                            }
                                            isContentLocked={isContentLocked}
                                            collectionSuspensionActive={
                                                collectionSuspensionActive
                                            }
                                        />
                                    </View>

                                    <Text
                                        testID={DETAIL_METADATA_ID}
                                        style={styles.metadataText}
                                        numberOfLines={1}
                                        {...(isWeb && { tabIndex: 0 })}
                                    >
                                        {metadata}
                                    </Text>
                                    {detailsMetadata?.Audio?.length !== 0 && (
                                        <View
                                            style={[
                                                gStyles.flexRow,
                                                gStyles.headerContainer,
                                                styles.metadataText,
                                                hasSubtitleData &&
                                                    styles.noMarginText,
                                            ]}
                                        >
                                            <Text
                                                style={styles.metadataText}
                                                testID={
                                                    DETAIL_AUDIO_LANGUAGE_TEXT
                                                }
                                                {...(isWeb && { tabIndex: 0 })}
                                            >
                                                {t(SETTINGS_AUDIO_LANGUAGE)}:{' '}
                                            </Text>

                                            {detailsMetadata?.Audio?.map(
                                                (language, index) =>
                                                    renderAudioSubtitlesAccessibilityText(
                                                        language,
                                                        index ===
                                                            detailsMetadata?.Audio!
                                                                .length -
                                                                1,
                                                        true,
                                                    ),
                                            )}
                                        </View>
                                    )}
                                    {hasSubtitleData && (
                                        <View
                                            style={[
                                                gStyles.flexRow,
                                                gStyles.headerContainer,
                                                styles.metadataText,
                                            ]}
                                        >
                                            <Text
                                                style={styles.metadataText}
                                                testID={DETAIL_SUBTITLES_TEXT}
                                                {...(isWeb && { tabIndex: 0 })}
                                            >
                                                {t(
                                                    SCREEN_AUDIO_SUBTITLES_SUBTITLES,
                                                )}
                                                :{' '}
                                            </Text>
                                            {detailsMetadata?.Subtitles?.map(
                                                (language, index) =>
                                                    renderAudioSubtitlesAccessibilityText(
                                                        language,
                                                        index ===
                                                            detailsMetadata?.Subtitles!
                                                                .length -
                                                                1,
                                                    ),
                                            )}
                                        </View>
                                    )}
                                </View>
                                {isTabletOrWeb ? (
                                    <>
                                        <DescriptionMore
                                            description={
                                                detailsMetadata.Description
                                            }
                                            onPress={handleShowDescription}
                                            numberOfLines={2}
                                            onDescriptionLoaded={
                                                onDescriptionLoaded
                                            }
                                            screenReaderEnabled={
                                                screenReaderEnabled
                                            }
                                        />
                                        {!isComingSoon && (
                                            <DetailPageButtons
                                                isChannelSubscribed={
                                                    isChannelSubscribed
                                                }
                                                item={detailsMetadata}
                                                isRecordingDropdownVisible={
                                                    isRecordingDropdownVisible
                                                }
                                                showRecordingDropdown={
                                                    showRecordingDropdown
                                                }
                                                hideRecordingDropdown={
                                                    hideRecordingDropdown
                                                }
                                                ableToPlayLive={ableToPlayLive}
                                                ableToPlayCatchup={
                                                    ableToPlayCatchup
                                                }
                                                ableToPlayVod={ableToPlayVod}
                                                primaryColor={primary}
                                                onWatchPress={handleWatchPress}
                                                isVod={isVod}
                                                bookmark={bookmark}
                                                onManageRecordingPress={
                                                    onManageRecordingPressCallback
                                                }
                                                canCloseButtonDropdown={
                                                    canCloseButtonDropdown
                                                }
                                                canWatchRecording={
                                                    detailsIsRecording &&
                                                    canWatchRecording
                                                }
                                                recordingsButtonsList={
                                                    recordingsButtonsList
                                                }
                                                hasGlobalRecordingPermission={
                                                    hasGlobalRecordingPermission
                                                }
                                                kidsProfileActive={
                                                    kidsProfileActive
                                                }
                                                videoDuration={videoDuration}
                                            />
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {!isComingSoon && (
                                            <DetailPageButtons
                                                item={detailsMetadata}
                                                isRecordingDropdownVisible={
                                                    isRecordingDropdownVisible
                                                }
                                                showRecordingDropdown={
                                                    showRecordingDropdown
                                                }
                                                hideRecordingDropdown={
                                                    hideRecordingDropdown
                                                }
                                                ableToPlayLive={ableToPlayLive}
                                                ableToPlayCatchup={
                                                    ableToPlayCatchup
                                                }
                                                primaryColor={primary}
                                                onWatchPress={handleWatchPress}
                                                isChannelSubscribed={
                                                    isChannelSubscribed
                                                }
                                                ableToPlayVod={ableToPlayVod}
                                                isVod={isVod}
                                                bookmark={bookmark}
                                                onManageRecordingPress={
                                                    onManageRecordingPressCallback
                                                }
                                                canCloseButtonDropdown={
                                                    canCloseButtonDropdown
                                                }
                                                canWatchRecording={
                                                    detailsIsRecording &&
                                                    canWatchRecording
                                                }
                                                recordingsButtonsList={
                                                    recordingsButtonsList
                                                }
                                                hasGlobalRecordingPermission={
                                                    hasGlobalRecordingPermission
                                                }
                                                kidsProfileActive={
                                                    kidsProfileActive
                                                }
                                                videoDuration={videoDuration}
                                            />
                                        )}
                                        <DescriptionMore
                                            onDescriptionLoaded={
                                                onDescriptionLoaded
                                            }
                                            description={
                                                detailsMetadata.Description
                                            }
                                            onPress={handleShowDescription}
                                            numberOfLines={2}
                                            screenReaderEnabled={
                                                screenReaderEnabled
                                            }
                                        />
                                    </>
                                )}
                            </View>
                            <View
                                style={
                                    isTablet &&
                                    shouldMoveTabletStripeUp && { top: -24 }
                                }
                            >
                                {hasSeasonStripe && (
                                    <Seasons
                                        channelId={channelId}
                                        seriesId={route.params.seriesId}
                                        templateId={templateId}
                                        isVod={isVod}
                                        detailsId={detailsId}
                                        initialSeason={initialSeason}
                                        onSelectEpisode={handleSelectEpisode}
                                    />
                                )}
                                {!!castAndCrew?.length && (
                                    <DetailCast
                                        castCrew={castAndCrew}
                                        style={
                                            hasSeasonStripe &&
                                            styles.extraMarginCast
                                        }
                                    />
                                )}
                            </View>
                        </View>
                    </Animated.ScrollView>
                    <RecordingsNotificationsAndOverlay
                        isRecordingLoading={isRecordingLoading}
                        seriesId={seriesId}
                        handleCancelRecording={handleCancelRecording}
                        handleCancelSeriesRecording={
                            handleCancelSeriesRecording
                        }
                        recordingsNotification={recordingsNotification}
                        showQuotaDialog={showQuotaDialog}
                        showCancelEpisodeDialog={showCancelEpisodeDialog}
                        showCancelSeriesDialog={showCancelSeriesDialog}
                        showDeleteRecordingDialog={
                            showDeleteRecordingDialog &&
                            !isNoInternetAlertVisible
                        }
                        showDeleteSeriesDialog={showDeleteSeriesDialog}
                        resetNotificationCallback={resetNotificationCallback}
                        hideCancelEpisodeDialogCallback={
                            hideCancelEpisodeDialogCallback
                        }
                        hideDeleteSeriesDialogCallback={
                            hideDeleteSeriesDialogCallback
                        }
                        hideDeleteRecordingDialogCallback={
                            hideDeleteRecordingDialogCallback
                        }
                        hideCancelSeriesDialogCallback={
                            hideCancelSeriesDialogCallback
                        }
                        hideQuotaDialogCallback={hideQuotaDialogCallback}
                        handleShowPinDialog={handleShowPinDialog}
                        pinDialogProps={pinDialogProps}
                        recordingIsProtected={recordingIsProtected}
                        seriesHasProtectedRecording={
                            seriesHasProtectedRecording
                        }
                        showDropdown={showRecordingDropdown}
                        onDeleteRecording={handleDeleteRecording}
                        onDeleteSeriesRecording={handleDeleteSeriesRecording}
                    />
                    <DescriptionOverlay
                        id={details.id!}
                        title={detailsMetadata.title || detailsMetadata.Title}
                        description={detailsMetadata.Description}
                        castCrew={castAndCrew}
                        onClose={
                            isWeb
                                ? handleHideDescriptionInBrowser
                                : handleHideDescription
                        }
                        visible={
                            descriptionVisible && !isNoInternetAlertVisible
                        }
                    />
                </>
            )}
            {(detailsLoading || descriptionLoading) && (
                <SkeletonLoading
                    isScreenSkeleton
                    testID={DETAIL_SKELETON_ID}
                    width={Platform.select({
                        web: widthPercentageToDP(400),
                        default: isTablet
                            ? widthPercentageToDP(400)
                            : Dimensions.get('screen').width,
                    })}
                    height={Platform.select({
                        web: heightPercentageToDP(276),
                        default: isTablet
                            ? heightPercentageToDP(296)
                            : Dimensions.get('screen').height,
                    })}
                    animatedBlockStyle={styles.skeletonLoading}
                    SkeletonLoadingComponent={Platform.select({
                        web: DetailSkeletonLoadingDesktop,
                        default: isTablet
                            ? DetailSkeletonLoadingTablet
                            : DetailSkeletonLoadingMobile,
                    })}
                    backgroundColor={background}
                />
            )}
        </DetailRootView>
    );
}

export default DetailPage;
